import { hasAnalyticsConsent } from "./cookieConsentStore";
import { logTrafficEvent } from "./firebaseClient";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

// Unset by default — no real ZenFresh GA4 property exists yet (see
// .env.example). Every function below is a permanent no-op until a real
// VITE_GA_MEASUREMENT_ID is provided, which trivially satisfies "decline
// (or never-configured) → zero analytics scripts load" for every visitor
// today, matching the SITE_URL/WHATSAPP_NUMBER inert-default pattern used
// elsewhere in this codebase.
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;

let ga4Loaded = false;

/**
 * Conditional script loading, not Google's full Consent Mode v2
 * default/update dance — this is a small marketing site with no other
 * Google tags that would need a pre-consent measurement baseline, so
 * simply never injecting gtag.js until real consent exists is the honest,
 * simpler mechanism for the same outcome. Idempotent and safe to call from
 * multiple places (CookieConsentProvider calls it on every consent change).
 */
export function loadGA4() {
  if (ga4Loaded || !GA_MEASUREMENT_ID || typeof document === "undefined") return;
  ga4Loaded = true;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer?.push(args);
  };
  window.gtag("js", new Date());
  // anonymize_ip: GA4 anonymizes by default, set explicitly anyway per the
  // AC's "excludes PII beyond GA4's own default anonymization" wording.
  window.gtag("config", GA_MEASUREMENT_ID, { anonymize_ip: true });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
}

/**
 * Every call site goes through this wrapper so the real-vs-simulated swap
 * stays in one file. Gated on cookie consent (ZP-80) independently of
 * loadGA4 — if consent is later revoked mid-session, no further events
 * fire even though the already-loaded script tag isn't retroactively
 * removed (matching how GA4's own consent revocation model works: stop
 * sending, don't try to unsend).
 *
 * Dual-writes to Firestore's `traffic_events` collection (see
 * firebaseClient.ts / firestore.rules) alongside GA4 — a real, live sink
 * even though GA4 itself is still waiting on a configured measurement ID.
 * Fire-and-forget: never awaited, never lets an analytics failure block or
 * throw into the caller.
 */
export function trackEvent(name: string, params: Record<string, string | number | boolean> = {}) {
  if (!hasAnalyticsConsent()) return;

  if (import.meta.env.DEV) {
    console.info(`[analytics] ${name}`, params);
  }
  window.gtag?.("event", name, params);
  void logTrafficEvent(name, params);
}
